import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import useBrowserTabTitle from '../hooks/useBrowserTabTitle';
import useGncHistory from '../hooks/useGncHistory';
import useSharePointToken from '../hooks/useSharePointToken';

const OFFICE_SCHEMES = {
    'ms-word': ['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm', 'odt', 'rtf'],
    'ms-excel': ['xls', 'xlsx', 'xlsm', 'xlsb', 'xlt', 'xltx', 'xltm', 'ods', 'csv'],
    'ms-powerpoint': ['ppt', 'pptx', 'pptm', 'pps', 'ppsx', 'ppsm', 'pot', 'potx', 'potm', 'odp'],
    'ms-visio': ['vsd', 'vsdx', 'vsdm', 'vst', 'vstx', 'vssx'],
    'ms-project': ['mpp', 'mpt'],
    'ms-publisher': ['pub'],
    'ms-access': ['accdb', 'mdb'],
    'onenote': ['one', 'onetoc2'],
};

const getFileExtension = (name, url) => {
    let candidate = name || '';

    if (!candidate && url) {
        try {
            candidate = decodeURIComponent(new URL(url).pathname);
        } catch (err) {
            candidate = url;
        }
    }

    const lastSegment = candidate.split(/[\\/]/).pop() || '';
    const dotIndex = lastSegment.lastIndexOf('.');

    if (dotIndex === -1) {
        return '';
    }

    return lastSegment.slice(dotIndex + 1).toLowerCase();
};

const getSchemeForExtension = (extension) => {
    if (!extension) {
        return null;
    }

    const entry = Object.entries(OFFICE_SCHEMES)
        .find(([, extensions]) => extensions.includes(extension));

    return entry ? entry[0] : null;
};

// isDocumentIDLink reports whether the URL is a SharePoint Document ID redirect,
// which has to be resolved to the real file URL before it can be opened.
const isDocumentIDLink = (href) => /_layouts\/(?:\d+\/)?(?:docidredir|doc)\.aspx/i.test(href || '');

// buildNativeUri returns the protocol handler URI that opens the document in
// its native Office application, or null when the file type is not supported.
const buildNativeUri = (webUrl, name) => {
    if (!webUrl) {
        return null;
    }

    const scheme = getSchemeForExtension(getFileExtension(name, webUrl));
    if (!scheme) {
        return null;
    }

    // OneNote uses a plain scheme, the Office apps use the 'ofe|u|' (open for editing) form
    if (scheme === 'onenote') {
        return `onenote:${webUrl}`;
    }

    return `${scheme}:ofe|u|${webUrl}`;
};

const SharePointOpenDocument = () => {
    useBrowserTabTitle('Open with SharePoint');

    const [searchParams] = useSearchParams();
    const href = searchParams.get('href');
    const returnUrl = searchParams.get('return_url');
    const entityType = searchParams.get('entity_type') || 'Document';
    const { goBack, addToHistory, getHistory } = useGncHistory();

    const {
        tokenIsValid,
        loading,
        encryptionKey,
        redirectToAuthUrl,
        sharePointApplicationIsValid,
        sharePointApplicationError,
        sharePointApplicationLoading,
        forceOpenWithNative,
    } = useSharePointToken();

    const [resolveLoading, setResolveLoading] = useState(false);
    const [resolveResult, setResolveResult] = useState(null);
    const [resolveError, setResolveError] = useState(null);
    const [opened, setOpened] = useState(false);

    // Seed GncHistory so that goBack returns to returnUrl minus the object id. Only relevant when this opens in a new tab
    useEffect(() => {
        if (!returnUrl) {
            return;
        }
        if (getHistory().length > 0) {
            return;
        }
        const parentUrl = returnUrl.includes('/')
            ? returnUrl.substring(0, returnUrl.lastIndexOf('/'))
            : returnUrl;
        addToHistory(parentUrl);
        addToHistory(returnUrl);
        // eslint-disable-next-line
    }, [returnUrl]);

    // Only Document ID links have to go through the API, any other URL already
    // points at the file itself.
    const needsResolve = forceOpenWithNative && isDocumentIDLink(href);

    // web_url is SharePoint's viewer link and is what a browser should open,
    // while edit_url is the direct path of the file, which is what the Office
    // protocol handlers need.
    const resolvedUrl = resolveResult
                      ? (resolveResult.web_url || resolveResult.webUrl)
                      : (forceOpenWithNative && !needsResolve ? href : null);
    const nativeUrl = resolveResult
                    ? (resolveResult.edit_url || resolvedUrl)
                    : resolvedUrl;
    // edit_url_alt is the sharing link form of the same file. Word accepts either
    // form, but which one works depends on the network the client is on, so it is
    // offered as a second link.
    const altNativeUrl = resolveResult ? resolveResult.edit_url_alt : null;
    const fileName = resolveResult ? resolveResult.name : null;
    const nativeUri = buildNativeUri(nativeUrl, fileName);
    const altNativeUri = buildNativeUri(altNativeUrl, fileName);

    // Anything that cannot, or should not, be opened natively is simply opened as-is
    useEffect(() => {
        if (!href || sharePointApplicationLoading) {
            return;
        }
        if (!forceOpenWithNative || (!needsResolve && !nativeUri)) {
            window.location.replace(href);
        }
    }, [href, sharePointApplicationLoading, forceOpenWithNative, needsResolve, nativeUri]);

    useEffect(() => {
        if (!needsResolve || !tokenIsValid || !href) {
            return;
        }

        setResolveLoading(true);
        setResolveResult(null);
        setResolveError(null);

        fetch('/v0/sharepoint/resolve_redirect?href=' + encodeURIComponent(href) + '&encryption_key=' + encodeURIComponent(encryptionKey), {
            credentials: 'same-origin',
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then((json) => {
                setResolveResult(json.result || json);
                setOpened(false);
            })
            .catch((err) => {
                setResolveError(err.message || String(err));
            })
            .finally(() => {
                setResolveLoading(false);
            });
    }, [needsResolve, tokenIsValid, href, encryptionKey]);

    useEffect(() => {
        if (!nativeUri || opened) {
            return;
        }

        setOpened(true);
        window.location.href = nativeUri;
    }, [nativeUri, opened]);

    if (sharePointApplicationLoading || (needsResolve && loading)) {
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>Open {entityType}</h2>
                            </div>
                            <div className="ibox-content">
                                <p>Loading SharePoint configuration...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!sharePointApplicationIsValid) {
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>SharePoint is not configured</h2>
                            </div>
                            <div className="ibox-content">
                                <p>SharePoint integration has not been configured in this environment.</p>
                                {sharePointApplicationError && (
                                    <p><strong>{sharePointApplicationError}</strong></p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (needsResolve && !tokenIsValid) {
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>Authenticate with SharePoint</h2>
                            </div>
                            <div className="ibox-content">
                                <p>
                                    You need to authenticate with SharePoint before you can open this document with the native application.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={redirectToAuthUrl}
                                >
                                    Authenticate with SharePoint
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!href) {
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>No document URL provided</h2>
                            </div>
                            <div className="ibox-content">
                                <p>We could not find a document to open.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Open {entityType}</h2>
                        </div>
                        <div className="ibox-content">
                            {resolveLoading && <p>Resolving SharePoint link...</p>}

                            {resolveError && (
                                <div className="alert alert-danger">
                                    <strong>Failed to resolve link:</strong> {resolveError}
                                </div>
                            )}

                            {nativeUri && (
                                <div>
                                    <div className="alert alert-info">
                                            <strong>Opening</strong> {fileName || resolvedUrl} in the native application...
                                    </div>
                                    <p>
                                        Hit OK to continue working in GNC Go, or select an Open button below to try again.
                                    </p>
                                    <p>
                                        <button className="btn btn-primary btn-mr" type="button" onClick={() => goBack()}>
                                            OK
                                        </button>
                                        <button className="btn btn-white btn-mr" type="button" onClick={() => { window.location.href = nativeUri; }}>
                                            Open in Native Application
                                        </button>
                                        <button className="btn btn-white btn-mr" type="button" onClick={() => window.open(resolvedUrl, '_blank', 'noopener,noreferrer')}>
                                            Open in Browser
                                        </button>
                                    </p>
                                </div>
                            )}

                            {resolveResult && !nativeUri && (
                                <div>
                                    <p>
                                        <strong>{fileName || resolvedUrl}</strong> is not an Office document that can be
                                        opened in a native application.
                                    </p>
                                    {resolvedUrl && (
                                        <p>
                                            <button className="btn btn-white btn-mr" type="button" onClick={() => goBack()}>
                                                OK
                                            </button>
                                            <button className="btn btn-primary btn-mr" type="button" onClick={() => window.open(resolvedUrl, '_blank', 'noopener,noreferrer')}>
                                                Open in Browser
                                            </button>
                                        </p>
                                    )}
                                </div>
                            )}

                            {!resolveLoading && !resolveError && !resolveResult && !nativeUri && (
                                <p>
                                   Opening <a href={href} target="_blank" rel="noreferrer">
                                        {href}
                                    </a> now.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SharePointOpenDocument;
